/**
 * The time-of-day greeting shown at the top of a landing surface.
 *
 * SHARED because there are two landing surfaces and a deployment renders
 * exactly one of them — `chat` (ChatHomeView, `features/workspace`) or
 * `launcher` (LauncherView, `features/launcher`). Those packages cannot import
 * each other, so a copy in each is the only alternative, and a copy is how the
 * two homes drift: a different hour boundary or a different wording makes the
 * same deployment greet the same person differently depending on a setting
 * they did not choose.
 *
 * ⚠ ENGLISH ONLY, deliberately not `t()`-ed — this preserves ChatHomeView's
 * existing behaviour rather than introducing a difference between the two
 * surfaces. It IS an i18n gap: every other string on both homes is
 * translated. Closing it means adding four keys to all four locales AND
 * changing what the chat home renders today, so it belongs to its own change
 * rather than riding along with the launcher's header.
 *
 * `Up late` before 05:00 rather than "Good night": at 2am the reader is
 * awake and using the product, so wishing them a good night is addressed to
 * someone who is not there.
 */
export function greetingForHour(h: number): string {
  if (h < 5) return 'Up late'
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

/** `Good morning, Alex` — or just the greeting when no name is known. */
export function greetingFor(hour: number, userName?: string | null): string {
  const firstName = userName?.trim().split(/\s+/)[0]
  return firstName ? `${greetingForHour(hour)}, ${firstName}` : greetingForHour(hour)
}
